// Fade overlay tween utility (wave 2E).
// Drives the #fade-overlay div in client/index.html via requestAnimationFrame.
// Pure helpers so they can be unit-tested without a real DOM by passing a
// mock element with a `style.opacity` field.

export interface FadeTarget {
  style: { opacity: string };
}

export interface TweenOptions {
  durationMs?: number;
  // Override for tests (no rAF, no performance.now). Returns ms.
  now?: () => number;
  // Override for tests. Called with a callback that should be invoked once
  // per "frame". Should return a cancellable-like (we ignore the return).
  schedule?: (cb: (ts: number) => void) => unknown;
}

const DEFAULT_DURATION_MS = 200;

export function fadeTo(
  el: FadeTarget | null,
  to: number,
  opts: TweenOptions = {},
): Promise<void> {
  if (!el) return Promise.resolve();
  const duration = Math.max(0, opts.durationMs ?? DEFAULT_DURATION_MS);
  const now = opts.now ?? (() => performance.now());
  const schedule = opts.schedule ?? ((cb) => requestAnimationFrame(cb));

  const from = clamp01(parseFloat(el.style.opacity || "0"));
  const target = clamp01(to);

  if (duration === 0) {
    el.style.opacity = String(target);
    return Promise.resolve();
  }

  const start = now();
  return new Promise<void>((resolve) => {
    function step(): void {
      const elapsed = now() - start;
      const t = elapsed >= duration ? 1 : elapsed / duration;
      const eased = easeOutCubic(t);
      el!.style.opacity = String(from + (target - from) * eased);
      if (t >= 1) {
        el!.style.opacity = String(target);
        resolve();
        return;
      }
      schedule(step);
    }
    schedule(step);
  });
}

export function fadeIn(el: FadeTarget | null, opts: TweenOptions = {}): Promise<void> {
  return fadeTo(el, 0.85, opts);
}

export function fadeOut(el: FadeTarget | null, opts: TweenOptions = {}): Promise<void> {
  return fadeTo(el, 0, opts);
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

function easeOutCubic(t: number): number {
  const x = 1 - t;
  return 1 - x * x * x;
}
